import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

export function useBackButtonHandler(enabled = true) {
  const { navigator } = useContext(UNSAFE_NavigationContext) || {};
  const handlingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [pendingTx, setPendingTx] = useState(null);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    let unblock = null;
    if (navigator && typeof navigator.block === "function") {
      unblock = navigator.block((tx) => {
        setPendingTx(tx);
        show();
      });
    }

    try {
      window.history.pushState({ _guard: true }, "", window.location.href);
    } catch {}

    const onAttemptLeave = () => {
      if (handlingRef.current) return;
      handlingRef.current = true;
      try {
        window.history.go(1);
      } finally {
        show();
        setTimeout(() => {
          handlingRef.current = false;
        }, 0);
      }
    };

    window.addEventListener("popstate", onAttemptLeave);
    window.addEventListener("hashchange", onAttemptLeave);

    return () => {
      window.removeEventListener("popstate", onAttemptLeave);
      window.removeEventListener("hashchange", onAttemptLeave);
      if (unblock) unblock();
    };
  }, [enabled, navigator, show]);

  const proceedOriginal = useCallback(() => {
    const tx = pendingTx;
    setPendingTx(null);
    hide();
    tx?.retry();
  }, [pendingTx, hide]);

  return { open, show, hide, proceedOriginal };
}
